import { useEffect, useRef, useState } from 'react';
import { HelpCircle, X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

interface Props {
  /** Caminho da imagem do guia (pasta `public`), ex: "/guias-modulos/okrs.png" */
  imagemSrc: string;
  /** Nome do módulo, usado no título do popup e no aria-label do botão */
  titulo: string;
}

const ESCALA_MIN = 1;
const ESCALA_MAX = 4;

/**
 * Botão flutuante "?" (canto inferior direito) que abre o guia visual do módulo em um popup
 * com zoom. Pensado pra ser colado em qualquer página de módulo, ex:
 *   <GuiaModuloFlutuante imagemSrc="/guias-modulos/okrs.png" titulo="OKRs" />
 *
 * Teste piloto (27-28/09) — só na página de OKRs. Depois de validado com a Samantha, replicar
 * pros outros módulos (ver guias-modulos/MODELO.md).
 */
export function GuiaModuloFlutuante({ imagemSrc, titulo }: Props) {
  const [aberto, setAberto] = useState(false);
  const [escala, setEscala] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [arrastandoUI, setArrastandoUI] = useState(false);
  const arrastando = useRef(false);
  const ultimoPonto = useRef({ x: 0, y: 0 });
  const pontosAtivos = useRef(new Map<number, { x: number; y: number }>());
  const distanciaPinchInicial = useRef<number | null>(null);
  const escalaPinchInicial = useRef(1);

  // Trava o scroll da página de fundo enquanto o popup está aberto
  useEffect(() => {
    if (!aberto) return;
    const overflowOriginal = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflowOriginal;
    };
  }, [aberto]);

  // ESC fecha
  useEffect(() => {
    if (!aberto) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberto(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [aberto]);

  const resetar = () => {
    setEscala(1);
    setPos({ x: 0, y: 0 });
  };

  const fechar = () => {
    setAberto(false);
    resetar();
  };

  const aplicarZoom = (delta: number) => {
    setEscala((atual) => Math.min(ESCALA_MAX, Math.max(ESCALA_MIN, atual + delta)));
  };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    aplicarZoom(e.deltaY < 0 ? 0.25 : -0.25);
  };

  const onDoubleClick = () => {
    if (escala > 1) {
      resetar();
    } else {
      setEscala(2.5);
    }
  };

  const distanciaEntre = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.hypot(a.x - b.x, a.y - b.y);

  const onPointerDown = (e: React.PointerEvent) => {
    pontosAtivos.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    if (pontosAtivos.current.size === 2) {
      const [p1, p2] = Array.from(pontosAtivos.current.values());
      distanciaPinchInicial.current = distanciaEntre(p1, p2);
      escalaPinchInicial.current = escala;
      arrastando.current = false;
      setArrastandoUI(false);
    } else if (pontosAtivos.current.size === 1) {
      // Arrastar pra ver a imagem inteira funciona em qualquer zoom (ela é mais alta que a
      // tela mesmo em 100%, então sem isso o topo/rodapé do guia ficava inacessível).
      arrastando.current = true;
      ultimoPonto.current = { x: e.clientX, y: e.clientY };
      setArrastandoUI(true);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pontosAtivos.current.has(e.pointerId)) return;
    pontosAtivos.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pontosAtivos.current.size === 2 && distanciaPinchInicial.current) {
      const [p1, p2] = Array.from(pontosAtivos.current.values());
      const distanciaAtual = distanciaEntre(p1, p2);
      const fator = distanciaAtual / distanciaPinchInicial.current;
      setEscala(Math.min(ESCALA_MAX, Math.max(ESCALA_MIN, escalaPinchInicial.current * fator)));
      return;
    }

    if (arrastando.current) {
      const dx = e.clientX - ultimoPonto.current.x;
      const dy = e.clientY - ultimoPonto.current.y;
      ultimoPonto.current = { x: e.clientX, y: e.clientY };
      setPos((atual) => ({ x: atual.x + dx, y: atual.y + dy }));
    }
  };

  const encerrarPonteiro = (e: React.PointerEvent) => {
    pontosAtivos.current.delete(e.pointerId);
    if (pontosAtivos.current.size < 2) distanciaPinchInicial.current = null;
    if (pontosAtivos.current.size === 0) {
      arrastando.current = false;
      setArrastandoUI(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setAberto(true)}
        aria-label={`Ver guia do módulo ${titulo}`}
        title={`Guia do módulo ${titulo}`}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 hover:shadow-xl active:scale-95"
      >
        <HelpCircle className="h-7 w-7" />
      </button>

      {aberto && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/80 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) fechar();
          }}
        >
          <div className="flex items-center justify-between gap-3 px-4 py-3 text-white sm:px-6">
            <span className="text-sm font-semibold sm:text-base">Guia do módulo — {titulo}</span>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={() => aplicarZoom(-0.5)}
                title="Diminuir zoom"
                className="rounded-full bg-white/10 p-2 hover:bg-white/20"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => aplicarZoom(0.5)}
                title="Aumentar zoom"
                className="rounded-full bg-white/10 p-2 hover:bg-white/20"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={resetar}
                title="Restaurar tamanho"
                className="rounded-full bg-white/10 p-2 hover:bg-white/20"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={fechar}
                title="Fechar"
                className="ml-1 rounded-full bg-white/10 p-2 hover:bg-white/20 sm:ml-2"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div
            className="relative flex-1 touch-none overflow-hidden select-none"
            style={{ cursor: arrastandoUI ? 'grabbing' : 'grab' }}
            onWheel={onWheel}
            onDoubleClick={onDoubleClick}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={encerrarPonteiro}
            onPointerLeave={encerrarPonteiro}
            onPointerCancel={encerrarPonteiro}
          >
            <img
              src={imagemSrc}
              alt={`Guia visual do módulo ${titulo}`}
              draggable={false}
              className="absolute left-1/2 top-1/2 max-h-none max-w-none rounded-lg shadow-2xl"
              style={{
                transform: `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px) scale(${escala})`,
                width: 'min(90vw, 820px)',
                transition: arrastandoUI ? 'none' : 'transform 100ms ease-out',
              }}
            />
          </div>

          <p className="px-4 pb-3 text-center text-xs text-white/60 sm:text-sm">
            Arraste pra ver a imagem inteira · role o mouse ou use os botões pra dar zoom · duplo clique alterna o zoom
          </p>
        </div>
      )}
    </>
  );
}
